import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import { useCompany } from "@/hooks/useCompany";
import { shouldNotifyOnce, appraisalCompletionKey, jobStatusChangeKey } from "@/lib/notificationIdempotency";

export const useJobNotifications = () => {
  const { toast } = useToast();
  const { isDriver, isAdmin } = useUserRole();
  const { companyId } = useCompany();

  useEffect(() => {
    // Driver notifications for new job assignments
    if (isDriver) {
      const setupDriverNotifications = async () => {
        try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get driver's record
        const { data: driverData } = await supabase
          .from("drivers")
          .select("id, company_id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!driverData) return;

        // Subscribe to new job assignments
        const channel = supabase
          .channel('driver-job-notifications')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'jobs',
              filter: `driver_id=eq.${driverData.id}`
            },
            (payload) => {
              console.log('New job assigned:', payload);
              
              toast({
                title: "New Job Assigned! 🚗",
                description: `You've been assigned a new job for ${payload.new.vehicle_make} ${payload.new.vehicle_model}. Check your Pending Jobs.`,
                duration: 8000,
              });
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'jobs',
              ...(driverData.company_id ? { filter: `company_id=eq.${driverData.company_id}` } : {}),
            },
            async (payload) => {
              // Check if this driver was just assigned to this job
              const wasAssignedToMe = 
                payload.new.driver_id === driverData.id && 
                payload.old.driver_id !== driverData.id;

              if (wasAssignedToMe) {
                console.log('Driver newly assigned to job:', payload);
                
                // Fetch full job details including customer info
                const { data: jobDetails } = await supabase
                  .from('jobs')
                  .select(`
                    vehicle_make,
                    vehicle_model,
                    vehicle_registration,
                    delivery_address,
                    customers (name)
                  `)
                  .eq('id', payload.new.id)
                  .single();

                if (jobDetails) {
                  const customerName = jobDetails.customers?.name || 'Customer';
                  
                  toast({
                    title: "New Job Assigned! 🚗",
                    description: `${jobDetails.vehicle_make} ${jobDetails.vehicle_model} (${jobDetails.vehicle_registration}) for ${customerName}. Check your Pending Jobs.`,
                    duration: 8000,
                  });

                  // Play notification sound
                  try {
                    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBD2W1/LIdy0FMYzT8dqIOQcXa7zs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw1Uq+btwGYcCDmR1/LMey0FKoPN8tiIOwcVarvs6KFQEw==');
                    audio.volume = 0.3;
                    audio.play().catch(() => {
                      // Ignore if browser blocks audio
                    });
                  } catch (error) {
                    // Ignore audio errors
                  }
                }
              } else if (payload.new.status !== payload.old.status && payload.new.driver_id === driverData.id) {
                // Job status changed for this driver. The key is the identity
                // of the transition and is shared with every other handler
                // that reports job status, so one transition can only ever
                // produce one toast — however many times it is delivered.
                if (shouldNotifyOnce(jobStatusChangeKey(payload.new.id, payload.new.status, payload.new.updated_at))) {
                  toast({
                    title: "Job Status Updated",
                    description: `Job status changed to ${payload.new.status}`,
                    duration: 5000,
                  });
                }
              }
            }
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
        } catch (e) {
          console.error("[JobNotifications] Driver setup error:", e);
          return undefined;
        }
      };

      const cleanup = setupDriverNotifications();
      
      return () => {
        cleanup.then((cleanupFn) => cleanupFn?.()).catch(() => {});
      };
    }

    // Admin notifications for appraisal completions
    if (isAdmin) {
      const setupAdminNotifications = async () => {
        try {
        // Subscribe to appraisal completions
        const channel = supabase
          .channel('admin-appraisal-notifications')
           .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'vehicle_appraisals',
              ...(companyId ? { filter: `company_id=eq.${companyId}` } : {}),
            },
            async (payload) => {
              try {
              // `vehicle_appraisals` publishes with REPLICA IDENTITY DEFAULT:
              // `payload.old` holds the primary key only, so `old.completed_at`
              // is ALWAYS undefined and every later update to a completed
              // appraisal (PDF reference, adopted photos, workflow writes) read
              // as a fresh completion. The transition is now identified by the
              // completion timestamp itself, and announced at most once — a
              // genuine re-completion carries a new timestamp and still fires.
              const alreadyCompleted = payload.old?.completed_at;
              if (payload.new.completed_at && !alreadyCompleted
                  && shouldNotifyOnce(appraisalCompletionKey(payload.new.id, payload.new.completed_at))) {
                console.log('Appraisal completed:', payload);
                
                
                // Fetch job details to show in notification
                const { data: jobData } = await supabase
                  .from('jobs')
                  .select('vehicle_make, vehicle_model, drivers(name)')
                  .eq('id', payload.new.job_id)
                  .single();

                const driverName = jobData?.drivers?.name || 'A driver';
                const appraisalType = payload.new.appraisal_type === 'collection' 
                  ? 'Proof of Collection' 
                  : payload.new.appraisal_type === 'delivery'
                  ? 'Proof of Delivery'
                  : 'Interior Appraisal';

                toast({
                  title: `${appraisalType} Complete! ✅`,
                  description: `${driverName} completed ${appraisalType} for ${jobData?.vehicle_make} ${jobData?.vehicle_model}`,
                  duration: 10000,
                });
              }
              } catch (e) {
                console.error("[JobNotifications] Admin notification handler error:", e);
              }
            }
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
        } catch (e) {
          console.error("[JobNotifications] Admin setup error:", e);
          return undefined;
        }
      };

      const cleanup = setupAdminNotifications();
      
      return () => {
        cleanup.then((cleanupFn) => cleanupFn?.()).catch(() => {});
      };
    }
  }, [isDriver, isAdmin, toast, companyId]);
};
